import React, { useCallback, useEffect, useState } from 'react';
import Page from '../../components/Page/Page';
import ActionsContainer from '../../components/ActionsContainer/ActionsContainer';
import EnvironmentMenu from '../../components/EnvironmentMenu/EnvironmentMenu';
import TimeFrameMenu from '../../components/TimeFrameMenu/TimeFrameMenu';
import ThemeButton from '../../components/ThemeButton/ThemeButton';
import useTimeFrame from '../../hooks/useTimeFrame';
import useEnvironment from '../../hooks/useEnvironment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useLiveCodeQuery from '../../hooks/useLiveCodeQuery';
import Treemap from './Treemap';
import useServices from '../../hooks/useServices';
import Overview from './Overview';
import DeadCodeTable from './DeadCodeTable';
import useAnalytics from '../../hooks/useAnalytics';
import { useGlobalQueryString } from '../../hooks/useGlobalFilter';

export default function LiveCodePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { timeFrame } = useTimeFrame();
  const { environment } = useEnvironment();
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const globalFilters = useGlobalQueryString();

  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(
    searchParams.get('serviceId')
      ? parseInt(searchParams.get('serviceId') as string) || null
      : null,
  );

  const handleServiceSelect = useCallback(
    (serviceId: number) => {
      setSelectedServiceId(serviceId);
      setSearchParams((prev) => {
        if (serviceId) {
          prev.set('serviceId', serviceId.toString());
        } else {
          prev.delete('serviceId');
        }
        return prev;
      });
    },
    [setSearchParams],
  );

  const liveCodeQuery = useLiveCodeQuery({
    serviceId: selectedServiceId,
    timeFrame,
    environmentName: environment?.name,
  });

  const servicesQuery = useServices({
    environmentName: environment?.name,
    timeFrame,
  });

  useEffect(() => {
    // once servicesQuery is loaded, select the first service if none is selected
    if (servicesQuery.isSuccess && selectedServiceId === null) {
      handleServiceSelect(servicesQuery.data?.services[0].id);
    }
  }, [
    servicesQuery.isSuccess,
    servicesQuery.data?.services,
    selectedServiceId,
    handleServiceSelect,
  ]);

  useEffect(() => {
    // if environment changes, check if the selected service is still available.
    // if not, select the first service
    if (servicesQuery.isSuccess && selectedServiceId) {
      const service = servicesQuery.data?.services.find(
        (service) => service.id === selectedServiceId,
      );
      if (!service) {
        handleServiceSelect(servicesQuery.data?.services[0].id);
      }
    }
  }, [
    servicesQuery.isSuccess,
    servicesQuery.data?.services,
    selectedServiceId,
    handleServiceSelect,
    environment,
  ]);

  useEffect(() => {
    if (selectedServiceId && environment?.name) {
      analytics.capture('live_code_viewed', {
        service_id: selectedServiceId,
        environment: environment?.name,
        service_name: servicesQuery.data?.services.find(
          (service) => service.id === selectedServiceId,
        )?.name as string,
      });
    }
  }, [
    analytics,
    environment?.name,
    selectedServiceId,
    servicesQuery.data?.services,
  ]);

  const handleFunctionClick = useCallback(
    (functionId: number) => {
      analytics.capture('live_code_function_clicked', {
        function_id: functionId,
        function_name: liveCodeQuery.data?.functions.find(
          (func) => func.id === functionId,
        )?.signature as string,
        service_id: selectedServiceId as number,
        service_name: servicesQuery.data?.services.find(
          (service) => service.id === selectedServiceId,
        )?.name as string,
        environment: environment?.name,
      });
      navigate(`/explore/functions/${functionId}?${globalFilters}`);
    },
    [
      analytics,
      environment?.name,
      globalFilters,
      liveCodeQuery.data?.functions,
      navigate,
      selectedServiceId,
      servicesQuery.data?.services,
    ],
  );

  if (liveCodeQuery.isError || servicesQuery.isError) {
    return (
      <Page
        header={'Live code'}
        actions={
          <ActionsContainer>
            <EnvironmentMenu />
            <TimeFrameMenu />
            <ThemeButton />
          </ActionsContainer>
        }
      >
        <div>Failed to load live code</div>
      </Page>
    );
  }

  return (
    <Page
      header={'Live code'}
      actions={
        <ActionsContainer>
          <EnvironmentMenu />
          <TimeFrameMenu />
          <ThemeButton />
        </ActionsContainer>
      }
    >
      <Overview
        loading={servicesQuery.isLoading || liveCodeQuery.isLoading}
        servicesLoading={servicesQuery.isLoading}
        services={servicesQuery.data?.services || []}
        functions={liveCodeQuery.data?.functions || []}
        selectedServiceId={selectedServiceId}
        onServiceSelect={handleServiceSelect}
      />
      <Treemap
        loading={servicesQuery.isLoading || liveCodeQuery.isLoading}
        functions={liveCodeQuery.data?.functions || []}
        onFunctionClick={handleFunctionClick}
      />
      <DeadCodeTable
        functions={liveCodeQuery.data?.functions || []}
        loading={liveCodeQuery.isLoading}
      />
    </Page>
  );
}
